import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useWishlist } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const MARK = "https://static.prod-images.emergentagent.com/jobs/65e76132-927f-451c-9933-4dc061721808/images/a347cbd409dd11d18d240db6cd3998b76442cc5a1a337d2840411b46446ca9a6.jpeg";

export default function ProductCard({ product }) {
  const { has, toggle } = useWishlist();
  const { addItem } = useCart();
  const { settings } = useSettings();
  const mark = mediaUrl(settings?.page_images?.brand_mark || MARK);
  const wished = has(product.id);
  const variants = product.variants || [];
  const soldOut = variants.every((v) => (v.stock || 0) <= 0);

  const quickAdd = (size) => {
    addItem(product, size, 1);
    toast.success(`Added ${product.title} (${size}).`, { icon: <img src={mark} alt="" className="w-5 h-5 object-contain mix-blend-multiply" /> });
  };

  return (
    <div className="group" data-testid={`product-card-${product.slug}`}>
      <div className="relative overflow-hidden bg-cream">
        <Link to={`/product/${product.slug}`} className="block">
          <img src={mediaUrl(product.images?.[0])} alt={product.title} className="w-full aspect-[3/4] object-cover vintage-photo transition-transform duration-700 group-hover:scale-105" />
        </Link>
        {product.compare_at_price && <span className="absolute top-3 left-3 bg-linen/90 text-ink text-[0.6rem] uppercase tracking-luxe px-3 py-1">Sale</span>}
        {soldOut && <span className="absolute top-3 left-3 bg-ink/90 text-linen text-[0.6rem] uppercase tracking-luxe px-3 py-1">Sold Out</span>}
        <button data-testid={`wishlist-toggle-${product.slug}`} onClick={() => toggle(product.id)}
                className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-linen/70 backdrop-blur-sm hover:bg-linen transition-colors icon-thin">
          <Heart size={16} className={wished ? "fill-ink text-ink" : "text-ink"} />
        </button>
        {!soldOut && (
          <div className="absolute bottom-0 inset-x-0 bg-linen/92 backdrop-blur-sm px-3 py-3 translate-y-full group-hover:translate-y-0 transition-transform duration-500" data-testid={`quick-add-${product.slug}`}>
            <p className="text-[0.55rem] uppercase tracking-wide2 text-ink/60 mb-2 text-center">Quick Add — Select Size</p>
            <div className="flex justify-center flex-wrap gap-1.5">
              {variants.map((v) => {
                const out = v.stock <= 0;
                return (
                  <button key={v.size} disabled={out} onClick={() => quickAdd(v.size)} data-testid={`quick-size-${product.slug}-${v.size}`}
                          className={`w-9 h-8 border font-body text-[0.65rem] transition-colors ${out ? "opacity-30 line-through border-ink/20 cursor-not-allowed" : "border-ink/30 hover:bg-ink hover:text-linen"}`}>{v.size}</button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className="pt-4 text-center">
        <Link to={`/product/${product.slug}`}><h3 className="font-heading text-xl leading-tight hover:opacity-70 transition-opacity">{product.title}</h3></Link>
        <div className="mt-1 font-body text-sm text-ink/70 flex items-center justify-center gap-2">
          <span>${product.price?.toFixed(2)}</span>
          {product.compare_at_price && <span className="line-through text-ink/40 text-xs">${product.compare_at_price.toFixed(2)}</span>}
        </div>
      </div>
    </div>
  );
}
